import { setDefaultModuleVersion, setDVMVersion } from '@3ddv/dvm-internal';
import { DOCUMENT } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MissingTranslationHandler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FailSafeTranslateLoader } from '@shared/i18n/fail-safe-translate.loader';
import { initializeI18nFactory } from '@shared/i18n/i18n.initializer';
import { ReportingMissingTranslationHandler } from '@shared/i18n/reporting-missing-translation.handler';
import { EnabledClientInterceptor } from '@shared/interceptors/client-enabled.interceptor';
import { OAuthModule, OAuthModuleConfig } from 'angular-oauth2-oidc';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ToastrModule } from 'ngx-toastr';
import { LoaderComponent } from 'src/app/shared/components/loader.component';
import { LoaderInterceptor } from 'src/app/shared/interceptors/loader.interceptor';
import { AuthService } from 'src/app/shared/services/auth.service';
import { MapsEditorModule } from './2d-maps-editor/maps-editor.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DashboardModule } from './dashboard/dashboard.module';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { BreadcrumbsComponent } from './layout/breadcrumbs/breadcrumbs.component';
import { FooterModule } from './layout/footer/footer.module';
import { AdminBarComponent } from './layout/header/admin-bar/admin-bar.component';
import { HeaderComponent } from './layout/header/header.component';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { MenuComponent } from './layout/menu/menu.component';
import { ChangePasswordComponent } from './login/change-password/change-password.component';
import { authModuleConfig } from './login/config/auth-module.config';
import { LoginComponent } from './login/login.component';
import { SeatingchartviewerModule } from './seatingchartviewer/seatingchartviewer.module';
import { ErrorPageComponent } from './shared/components/error-page/error-page.component';
import { CDResponseInterceptor } from './shared/interceptors/cd-response-adapter.interceptor';
import { ValidationInterceptor } from './shared/interceptors/validation.interceptor';
import { DeleteElementComponent } from './shared/modals/delete-element/delete-element.component';
import { MessagingModalComponent } from './shared/modals/messaging-modal/messaging-modal.component';
import { MessagingService } from './shared/modals/messaging-modal/service/messaging.service';
import { ApiService } from './shared/services/api.service';
import { SitesBuilderModule } from './sites-builder/sites-builder.module';

export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
  return new FailSafeTranslateLoader(new TranslateHttpLoader(http, './assets/i18n/', '.json'));
}

function initializeAuthFactory(authService: AuthService) {
  return () => authService.configureAuth();
}

setDVMVersion("3ddv");
setDefaultModuleVersion("map_viewer", "3ddv");
setDefaultModuleVersion("3d_viewer", "3ddv");

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    MainLayoutComponent,
    DeleteElementComponent,
    MessagingModalComponent,
    BreadcrumbsComponent,
    LoginComponent,
    ChangePasswordComponent,
    MenuComponent,
    AdminBarComponent,
    ForgotPasswordComponent,
    ErrorPageComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    DashboardModule,
    MapsEditorModule,
    FormsModule,
    ToastrModule.forRoot(),
    BrowserAnimationsModule,
    ModalModule.forRoot(),
    HttpClientModule,
    CarouselModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: ReportingMissingTranslationHandler },
    }),
    SitesBuilderModule,
    SeatingchartviewerModule,
    BsDropdownModule.forRoot(),
    TooltipModule.forRoot(),
    FooterModule,
    OAuthModule.forRoot(),
    LoaderComponent
  ],
  providers: [
    BsModalRef,
    AuthService,
    ApiService,
    MessagingService,
    { provide: HTTP_INTERCEPTORS, useClass: ValidationInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CDResponseInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: EnabledClientInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: DebugInterceptor, multi: true},
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: OAuthModuleConfig, useValue: authModuleConfig },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuthFactory,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeI18nFactory,
      deps: [TranslateService, DOCUMENT],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
